import { useFindMany } from "@gadgetinc/react";
import { ArrowLeftIcon, ArrowRightIcon } from "@heroicons/react/16/solid";
import { EllipsisHorizontalIcon, PlusIcon } from "@heroicons/react/20/solid";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "../../api";
import { OverflowFrame, PageHeading } from "../../externalComponents";
import LoadingSpinner from "../../externalComponents/LoadingSpinner";
import ShareModal from "../../externalComponents/ShareModal";
import ErrorModal from "../../externalComponents/ErrorModal";
import { Badge } from "../../externalComponents/tailwind/badge";
import { Button } from "../../externalComponents/tailwind/button";
import {
  Dialog,
  DialogActions,
  DialogBody,
  DialogTitle,
} from "../../externalComponents/tailwind/dialog";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownMenu,
} from "../../externalComponents/tailwind/dropdown";
import EmptyState from "../../externalComponents/tailwind/empty-state";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../externalComponents/tailwind/table";
import { CustomerContext } from "../../providers";
import { useIsMobile } from "../../externalComponents/useIsMobile";
import { BannerNewLibraryFlow } from "../../externalComponents/BannerNewLibraryFlow";
import { getPromotedLibraryFlows } from "../../functions/getPromotedLibraryFlows";

const CampaignsPage = () => {
  const { user, customer, integrations } = useContext(CustomerContext);
  const [search, setSearch] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isModalDuplicateOpen, setIsModalDuplicateOpen] = useState(false);
  const [isModalShareOpen, setIsModalShareOpen] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [isArchiveLoading, setIsArchiveLoading] = useState(false);
  const [isDuplicateLoading, setIsDuplicateLoading] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const hasTurnover = integrations.shopify || integrations.splio;

  const isMobile = useIsMobile();

  const handleArchiverClick = (campaign) => {
    setSelectedCampaign(campaign);
    setIsModalOpen(true);
  };

  const handleArchiveConfirmation = async () => {
    setIsArchiveLoading(true);

    if (selectedCampaign) {
      await api.campaign.update(selectedCampaign.id, {
        isArchived: true,
      });
    }

    setIsModalOpen(false);
    setIsArchiveLoading(false);
  };

  const handleDuplicateClick = (campaign) => {
    setSelectedCampaign(campaign);
    setIsModalDuplicateOpen(true);
  };

  const handleDuplicateConfirmation = async () => {
    setIsDuplicateLoading(true);
    try {
      const newCampaign = await api.duplicateCampaignNodesAndEdges({
        oldCampaignId: selectedCampaign.id,
        customerId: customer.id,
      });
      navigate(`/campaigns/edit/${newCampaign.id}`);
    } catch (error) {
      setIsModalDuplicateOpen(false);
      setIsErrorModalOpen(true);
    } finally {
      setIsDuplicateLoading(false);
    }
  };

  const handleErrorModalClose = () => {
    setIsErrorModalOpen(false);
  };

  const NUM_ON_PAGE = 10;
  const [cursor, setCursor] = useState({ first: NUM_ON_PAGE });
  const [pageNumber, setPageNumber] = useState(1);
  const navigate = useNavigate();
  const location = useLocation();
  const [hasFetchedAllData, setHasFetchedAllData] = useState(false);

  // URL parameter handling states
  const [targetPageFromUrl, setTargetPageFromUrl] = useState(null);
  const [isNavigatingFromUrl, setIsNavigatingFromUrl] = useState(false);
  const [isLoadingToTargetPage, setIsLoadingToTargetPage] = useState(false);

  const _defaultPartialFields = {
    id: true,
    name: true,
    updatedAt: true,
    trigger: true,
    sendingsStatus: true,
    scenario: {
      id: true,
    },
  };
  const [fields, setFields] = useState(_defaultPartialFields);

  const [criteria, setCriteria] = useState({
    ...cursor,
    filter: [
      { customerId: { equals: user.customerId } },
      { trigger: { equals: "manual" } },
      { isArchived: { notEquals: true } },
    ],
  });

  const [{ data: searchData }] = useFindMany(api.campaign, {
    search,
    select: { id: true },
  });

  useEffect(() => {
    if (search) {
      if (searchData) {
        const ids = searchData.map(({ id }) => id);
        setCriteria({
          filter: [
            { customerId: { equals: user.customerId } },
            { id: { in: ids.length > 0 ? ids : [0] } },
            { trigger: { equals: "manual" } },
            { isArchived: { notEquals: true } },
          ],
        });
        setFields(_defaultPartialFields);
        setHasFetchedAllData(false);
      }
    } else {
      setCriteria({
        ...cursor,
        filter: [
          { customerId: { equals: user.customerId } },
          { trigger: { equals: "manual" } },
          { isArchived: { notEquals: true } },
        ],
      });
      setFields(_defaultPartialFields);
      setHasFetchedAllData(false);
    }
  }, [searchData, search]);

  // Initialize page number from URL parameter
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const pageParam = searchParams.get("page");
    const initialPage = pageParam ? parseInt(pageParam, 10) : 1;

    if (initialPage > 1) {
      setTargetPageFromUrl(initialPage);
      setIsNavigatingFromUrl(true);
      setIsLoadingToTargetPage(true);
    } else {
      setTargetPageFromUrl(null);
      setIsNavigatingFromUrl(false);
      setIsLoadingToTargetPage(false);
    }
  }, [location.search]);

  const [{ data, fetching, error }] = useFindMany(api.campaign, {
    ...criteria,
    select: fields,
    sort: {
      updatedAt: "Descending",
    },
  });

  useEffect(() => {
    if (!fetching && !hasFetchedAllData) {
      setFields({
        ..._defaultPartialFields,
        sent: true,
        delivered: true,
        read: true,
        externalTurnover: true,
        attributedRevenueShopify: true,
        attributedOrdersShopify: true,
        scheduleDate: true,
        scheduledDate: true,
      });
      setHasFetchedAllData(true);
    }
    if (error && hasFetchedAllData) {
      setFields(_defaultPartialFields);
    }
  }, [data, error]);

  const changePage = useCallback(
    (_cursor, _pageNumber) => {
      setCursor(_cursor);
      setPageNumber(_pageNumber);

      // Only update URL parameter if we're not navigating from URL
      if (!isNavigatingFromUrl) {
        const searchParams = new URLSearchParams(location.search);
        if (_pageNumber === 1) {
          searchParams.delete("page");
        } else {
          searchParams.set("page", _pageNumber.toString());
        }

        const newSearch = searchParams.toString();
        const newUrl = newSearch
          ? `${location.pathname}?${newSearch}`
          : location.pathname;
        navigate(newUrl, { replace: true });
      }

      setCriteria({
        ..._cursor,
        filter: [
          { customerId: { equals: user.customerId } },
          { trigger: { equals: "manual" } },
          { isArchived: { notEquals: true } },
        ],
      });
      setFields(_defaultPartialFields);
      setHasFetchedAllData(false);
    },
    [
      location.search,
      location.pathname,
      navigate,
      user.customerId,
      isNavigatingFromUrl,
    ],
  );

  const getNextPage = () => {
    if (data?.endCursor) {
      changePage({ first: NUM_ON_PAGE, after: data.endCursor }, pageNumber + 1);
    }
  };

  // Handle navigation to correct page from URL parameter
  useEffect(() => {
    if (
      targetPageFromUrl &&
      targetPageFromUrl > 1 &&
      data &&
      data.length > 0 &&
      pageNumber < targetPageFromUrl &&
      !fetching
    ) {
      // We have data and need to navigate to the target page
      // Call getNextPage to move to the next page
      if (data?.endCursor) {
        getNextPage();
      }
    } else if (targetPageFromUrl && pageNumber === targetPageFromUrl) {
      // We've reached the target page, clear the target and navigation flag
      setTargetPageFromUrl(null);
      setIsNavigatingFromUrl(false);
      setIsLoadingToTargetPage(false);
    }
  }, [data, targetPageFromUrl, pageNumber, fetching]);

  const getPreviousPage = () => {
    if (pageNumber > 1) {
      changePage(
        { last: NUM_ON_PAGE, before: data.startCursor },
        pageNumber - 1,
      );
    }
  };

  const [dataLibraryFlowCampaigns, setDataLibraryFlowCampaigns] = useState([]);

  useEffect(() => {
    const loadPromotedFlows = async () => {
      try {
        const campaigns = await getPromotedLibraryFlows({
          isAutomation: false,
        });

        setDataLibraryFlowCampaigns(campaigns);
      } catch (error) {
        console.error("Erreur lors du chargement des flows promus:", error);
      }
    };

    loadPromotedFlows();
  }, []);

  return (
    <>
      <PageHeading
        title="Campaigns"
        searchPlaceholder="Search in campaigns"
        search={search}
        setSearch={setSearch}
        iconPrimaryAction={<PlusIcon />}
        {...(!isMobile && {
          primaryAction: "Create",
          handlePrimaryAction: async () => {
            navigate("/campaigns/new");
          },
        })}
        secondaryAction={`Sendings${
          user?.customer?.scheduledCampaignCount > 0
            ? ` (${user.customer.scheduledCampaignCount})`
            : ""
        }`}
        handleSecondaryAction={async () => {
          navigate("/campaigns/sendings");
        }}
      />
      {isLoadingToTargetPage && <LoadingSpinner />}
      <OverflowFrame>
        {!isMobile && data && data.length === 0 && (
          <BannerNewLibraryFlow
            dataLibraryFlow={dataLibraryFlowCampaigns}
            hasAutomations={false}
            title="Recommended campaigns"
            user={user}
          />
        )}
        {data && !isLoadingToTargetPage && (
          <>
            {data.length > 0 ? (
              <Table className="mx-3 md:mx-0">
                <TableHead>
                  <TableRow>
                    <TableHeader
                      className={`${isMobile ? "w-[65%]" : hasTurnover ? "w-[30%]" : "w-[60%]"}`}
                    >
                      Name
                    </TableHeader>
                    <TableHeader className="text-center">Status</TableHeader>
                    {!isMobile && (
                      <>
                        <TableHeader className="text-center">
                          Last edited
                        </TableHeader>
                        <TableHeader className="text-center">
                          Sendings
                        </TableHeader>
                        {hasTurnover && (
                          <>
                            <TableHeader className="text-center">
                              Turnover
                            </TableHeader>
                            <TableHeader className="text-center">
                              Performance
                            </TableHeader>
                            <TableHeader className="text-center">
                              ARPC
                            </TableHeader>
                          </>
                        )}
                        <TableHeader className="text-center">
                          Actions
                        </TableHeader>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {data.map((campaign) => {
                    const SendingStatus = () => {
                      const isScheduled = campaign.sendingsStatus?.some(
                        ({ scheduled }) => scheduled,
                      );
                      const isSent = campaign.sendingsStatus?.some(
                        ({ sent }) => sent,
                      );

                      return isScheduled ? (
                        <>
                          <Badge color="violet" className="ml-2">
                            Scheduled
                          </Badge>

                          <div className="text-xs">
                            {campaign.scheduleDate &&
                              new Date(
                                campaign.scheduleDate,
                              ).toLocaleDateString("en-US", {
                                day: "numeric",
                                month: "short",
                                hour: "numeric",
                                minute: "numeric",
                              })}
                          </div>
                        </>
                      ) : isSent ? (
                        <>
                          <Badge color="lime" className="ml-2">
                            Sent
                          </Badge>
                          <div className="text-xs">
                            {campaign.scheduledDate &&
                              new Date(
                                campaign.scheduledDate,
                              ).toLocaleDateString("en-US", {
                                day: "numeric",
                                month: "short",
                                hour: "numeric",
                                minute: "numeric",
                              })}
                          </div>
                        </>
                      ) : (
                        <Badge color="sky" className="ml-2">
                          Draft
                        </Badge>
                      );
                    };

                    const calculateArpc = () => {
                      if (!campaign.sent) return "-";
                      if (campaign.attributedRevenueShopify && campaign.sent) {
                        return (
                          campaign.attributedRevenueShopify / campaign.sent
                        ).toFixed(2);
                      }
                      if (campaign?.externalTurnover > 0) {
                        return (
                          campaign.externalTurnover / campaign.sent
                        ).toFixed(2);
                      }
                      return "-";
                    };

                    const arpc = calculateArpc();
                    let isScheduled = campaign.sendingsStatus?.some(
                      (s) => s.scheduled === true,
                    );
                    return (
                      <TableRow
                        className="h-[72px]"
                        key={campaign.id}
                        href={
                          isMobile
                            ? `/analytics/${campaign.id}`
                            : `/campaigns/edit/${campaign.id}`
                        }
                      >
                        <TableCell className="w-[25%]">
                          {campaign.name}
                        </TableCell>
                        <TableCell className="text-center">
                          <SendingStatus />
                        </TableCell>
                        {!isMobile && (
                          <>
                            <TableCell className="text-center">
                              {campaign.updatedAt &&
                                campaign.updatedAt.toLocaleDateString("en-US", {
                                  day: "numeric",
                                  month: "short",
                                  hour: "numeric",
                                  minute: "numeric",
                                })}
                            </TableCell>
                            <TableCell className="text-center">
                              {campaign.sent === undefined ? undefined : (
                                <>
                                  {campaign.sent ? campaign.sent : "-"}

                                  {campaign.delivered &&
                                  campaign.delivered > 0 ? (
                                    <div className="text-xxs font-bold leading-none">
                                      {(
                                        (Math.min(
                                          campaign.read,
                                          campaign.delivered,
                                        ) /
                                          campaign.delivered) *
                                        100
                                      ).toFixed(0)}
                                      % read
                                    </div>
                                  ) : null}
                                </>
                              )}
                            </TableCell>
                            {hasTurnover && (
                              <>
                                <TableCell className="text-center">
                                  {campaign.delivered ===
                                  undefined ? undefined : (
                                    <>
                                      {campaign.delivered
                                        ? campaign.attributedRevenueShopify
                                          ? campaign.attributedRevenueShopify.toFixed(
                                              0,
                                            ) + "€"
                                          : campaign.externalTurnover > 0
                                            ? campaign.externalTurnover.toFixed(
                                                0,
                                              ) + "€"
                                            : "-"
                                        : "-"}
                                      {campaign.delivered &&
                                      campaign.attributedOrdersShopify > 0 ? (
                                        <div className="text-xxs font-bold leading-none">
                                          {campaign.attributedOrdersShopify}{" "}
                                          sale
                                          {campaign.attributedOrdersShopify > 1
                                            ? "s"
                                            : ""}
                                        </div>
                                      ) : null}
                                    </>
                                  )}
                                </TableCell>
                                <TableCell className="text-center">
                                  {campaign.delivered ===
                                  undefined ? undefined : (
                                    <>
                                      {arpc !== "-" && arpc > 0 ? (
                                        <Badge
                                          color={
                                            arpc > 0 && arpc < 1
                                              ? "indigo"
                                              : arpc >= 1 && arpc < 2.5
                                                ? "sky"
                                                : "lime"
                                          }
                                        >
                                          {arpc > 0 && arpc < 1
                                            ? "Fair"
                                            : arpc >= 1 && arpc < 2.5
                                              ? "Good"
                                              : "Great"}
                                        </Badge>
                                      ) : (
                                        "-"
                                      )}
                                    </>
                                  )}
                                </TableCell>
                                <TableCell className="text-center">
                                  {campaign.delivered ===
                                  undefined ? undefined : (
                                    <>
                                      {arpc > 0 ? `${arpc} €` : "-"}
                                      {arpc > 0 && (
                                        <div className="text-xxs font-bold leading-none">
                                          per conversation
                                        </div>
                                      )}
                                    </>
                                  )}
                                </TableCell>
                              </>
                            )}
                            <TableCell className="text-center">
                              <div className="-mx-3 -my-1.5 sm:-mx-2.5">
                                <Dropdown>
                                  <DropdownButton
                                    plain
                                    aria-label="More options"
                                  >
                                    <EllipsisHorizontalIcon />
                                  </DropdownButton>

                                  <DropdownMenu anchor="bottom end">
                                    <DropdownItem
                                      onClick={() =>
                                        navigate(`/analytics/${campaign.id}`)
                                      }
                                    >
                                      Analytics
                                    </DropdownItem>
                                    {isScheduled && (
                                      <DropdownItem
                                        onClick={() =>
                                          api.campaign.cancelFutureSendings(
                                            campaign.id,
                                          )
                                        }
                                      >
                                        Cancel future sendings
                                      </DropdownItem>
                                    )}
                                    <DropdownItem
                                      onClick={() =>
                                        navigate(
                                          `/campaigns/send/${campaign.id}`,
                                        )
                                      }
                                    >
                                      Schedule
                                    </DropdownItem>
                                    <DropdownItem
                                      onClick={() =>
                                        navigate(
                                          `/campaigns/edit/${campaign.id}`,
                                        )
                                      }
                                    >
                                      Edit
                                    </DropdownItem>
                                    <DropdownItem
                                      onClick={() =>
                                        handleDuplicateClick(campaign)
                                      }
                                    >
                                      Duplicate
                                    </DropdownItem>
                                    <DropdownItem
                                      onClick={() =>
                                        handleArchiverClick(campaign)
                                      }
                                    >
                                      Archive
                                    </DropdownItem>
                                  </DropdownMenu>
                                </Dropdown>
                              </div>
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            ) : (
              <EmptyState
                heading="You have no campaigns"
                action={async () => {
                  if (customer.isSubscribed) {
                    navigate("/campaigns/new");
                  }
                }}
                icon={<PlusIcon />}
                actionText="Create"
                img="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
              />
            )}
          </>
        )}
        {!search && !isLoadingToTargetPage && (
          <div className="flex justify-start my-4">
            <Button
              color="secondaryButton"
              onClick={getPreviousPage}
              disabled={!data?.hasPreviousPage}
            >
              <ArrowLeftIcon />
              Previous
            </Button>
            <Badge className="my-1 mx-2 min-w-10 flex items-center justify-center">
              {pageNumber}
            </Badge>
            <Button
              color="secondaryButton"
              onClick={getNextPage}
              disabled={!data?.hasNextPage}
            >
              Next
              <ArrowRightIcon />
            </Button>
          </div>
        )}
      </OverflowFrame>

      {/* TODO Use DialogArchive */}
      <Dialog
        key="dialog_archive"
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      >
        <DialogTitle>Archive this campaign </DialogTitle>
        <DialogBody className="flex-1 overflow-y-auto scrollbar">
          <p>
            Are you sure you want to archive the campaign{" "}
            <b>{selectedCampaign?.name}</b>?
          </p>
        </DialogBody>
        <DialogActions>
          <Button
            color="secondaryButton"
            onClick={() => setIsModalOpen(false)}
            disabled={isArchiveLoading}
            className="min-w-[100px] h-10"
          >
            Cancel
          </Button>
          <Button
            color="primaryButton"
            onClick={() => handleArchiveConfirmation()}
            className="min-w-[100px] h-10"
            loading={isArchiveLoading}
          >
            Archive
          </Button>
        </DialogActions>
      </Dialog>

      <ShareModal
        campaign={selectedCampaign}
        scenarioId={selectedCampaign?.scenario.id}
        isModalShareOpen={isModalShareOpen}
        setIsModalShareOpen={setIsModalShareOpen}
        isAutomation={false}
      />

      {/* TODO Use DialogDuplicate */}
      <Dialog
        key="dialog_duplicate"
        open={isModalDuplicateOpen}
        onClose={() => {
          if (!isDuplicateLoading) {
            setIsModalDuplicateOpen(false);
          }
        }}
      >
        <DialogTitle close={!isDuplicateLoading}>
          Duplicate this campaign{" "}
        </DialogTitle>
        <DialogBody className="flex-1 overflow-y-auto scrollbar">
          <p>
            Are you sure you want to duplicate the campaign{" "}
            <b>{selectedCampaign?.name}</b>?
          </p>
        </DialogBody>
        <DialogActions>
          <Button
            color="secondaryButton"
            onClick={() => setIsModalDuplicateOpen(false)}
            disabled={isDuplicateLoading}
            className="min-w-[100px] h-10"
          >
            Cancel
          </Button>
          <Button
            color="primaryButton"
            onClick={() => handleDuplicateConfirmation()}
            className="min-w-[100px] h-10"
            loading={isDuplicateLoading}
          >
            Duplicate
          </Button>
        </DialogActions>
      </Dialog>

      <ErrorModal
        open={isErrorModalOpen}
        onClose={handleErrorModalClose}
        errorType={"duplicate"}
      />
    </>
  );
};
export default CampaignsPage;
